import {
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREVIEW_MAX_FRAME_COUNT,
  SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREVIEW_ROTATION_DEGREES,
} from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';

const clampProgress = (progress: number) => Math.max(0, Math.min(1, progress));

export const getThreesixtyPreviewMaxFrame = (frameCount: number) => {
  if (frameCount <= 0) return 0;

  return Math.min(
    frameCount - 1,
    Math.floor(
      (frameCount *
        SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREVIEW_ROTATION_DEGREES) /
        360
    )
  );
};

export const getThreesixtyPreviewPlaybackFrameSequence = (
  frameCount: number
) => {
  if (frameCount <= 0) return [];

  const maxFrame = getThreesixtyPreviewMaxFrame(frameCount);
  const sequenceLength = Math.min(
    maxFrame + 1,
    SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREVIEW_MAX_FRAME_COUNT
  );
  if (sequenceLength <= 1) return [0];

  return Array.from({ length: sequenceLength }, (_, index) =>
    Math.round((index * maxFrame) / (sequenceLength - 1))
  );
};

export const getThreesixtyPreviewFrameIndex = (
  progress: number,
  frameCount: number
) => {
  const sequence = getThreesixtyPreviewPlaybackFrameSequence(frameCount);
  const currentProgress = clampProgress(progress);
  if (sequence.length === 0) return 0;

  const sequenceIndex = Math.round(
    (1 - currentProgress) * (sequence.length - 1)
  );

  return sequence[sequenceIndex];
};

export const getThreesixtyPreviewStartFrameIndex = (frameCount: number) =>
  getThreesixtyPreviewMaxFrame(frameCount);

export const getNextThreesixtyPreviewFrameIndex = (
  currentIndex: number,
  targetIndex: number,
  frameCount: number
) => {
  if (currentIndex === targetIndex) return currentIndex;

  const sequence = getThreesixtyPreviewPlaybackFrameSequence(frameCount);
  const currentSequenceIndex = sequence.indexOf(currentIndex);
  const targetSequenceIndex = sequence.indexOf(targetIndex);
  if (currentSequenceIndex < 0 || targetSequenceIndex < 0) return targetIndex;

  return sequence[
    currentSequenceIndex + Math.sign(targetSequenceIndex - currentSequenceIndex)
  ];
};

export const getInterpolatedThreesixtyPreviewFrameIndex = (
  fromIndex: number,
  toIndex: number,
  frameCount: number,
  progress: number
) => {
  const sequence = getThreesixtyPreviewPlaybackFrameSequence(frameCount);
  const fromSequenceIndex = sequence.indexOf(fromIndex);
  const toSequenceIndex = sequence.indexOf(toIndex);
  if (fromSequenceIndex < 0 || toSequenceIndex < 0) {
    return progress >= 1 ? toIndex : fromIndex;
  }

  const nextSequenceIndex = Math.round(
    fromSequenceIndex +
      (toSequenceIndex - fromSequenceIndex) * clampProgress(progress)
  );

  return sequence[nextSequenceIndex] ?? toIndex;
};

export const getThreesixtyPreviewPlaybackFrameIndexes = (
  frameCount: number
) => {
  return new Set(getThreesixtyPreviewPlaybackFrameSequence(frameCount));
};

export const getThreesixtyPreviewFrameIndexes = (frameCount: number) => {
  return getThreesixtyPreviewPlaybackFrameIndexes(frameCount);
};
